<template>
  <TransitionRoot as="template" :show="checkoutStore.isCartOpen">
    <Dialog
      ref="dialog"
      as="div"
      class="relative z-[999]"
      @close="closeWithDelay"
    >
      <div class="fixed inset-0 top-0 overflow-hidden">
        <div class="absolute inset-0 top-0 overflow-hidden">
          <div
            class="pointer-events-none fixed bottom-0 right-0 top-0 flex max-w-full"
          >
            <TransitionChild
              as="template"
              enter="transform transition ease-in-out duration-250"
              enter-from="translate-x-full"
              enter-to="translate-x-0"
              leave="transform transition ease-in-out duration-250"
              leave-from="translate-x-0"
              leave-to="translate-x-full"
              @after-enter="onAfterEnter"
            >
              <DialogPanel
                class="pointer-events-auto relative w-screen max-w-[91.75%] md:max-w-[500px] ml-auto"
              >
                <div
                  class="flex h-full flex-col overflow-hidden bg-black text-white shadow-xl"
                >
                  <div
                    class="border-b-black-40 flex items-center justify-between border-b p-4"
                  >
                    <DialogTitle class="ui-text-xs uppercase">
                      {{ $t("yourCart") }}
                    </DialogTitle>
                    <UiButton
                      variant-type="icon"
                      variant="clear"
                      size="xs"
                      class="text-white size-4"
                      :aria-label="
                        $t('ariaLabelClose', { title: $t('yourShoppingBag') })
                      "
                      @click="checkoutStore.isCartOpen = false"
                    >
                      <UiIcon name="close" class="size-6" />
                    </UiButton>
                  </div>
                  <template v-if="cartInfo">
                    <div
                      class="styled-scrollbars relative flex-1 overflow-auto px-4"
                    >
                      <template v-if="!isEmpty(cartItemsGrouped)">
                        <div class="uppercase pt-8 pb-4 ui-text-sm font-medium">
                          {{ $t("arrivesBy", { date: estimateShipping }) }}
                        </div>
                        <LazyCartProductCard
                          v-for="(product, index) in cartItemsGrouped"
                          :key="product.id"
                          :product="product"
                          :product-index="index"
                          white
                        />
                      </template>
                      <div v-else class="pt-6 text-center text-xl">
                        {{ $t("cartEmpty") }}
                      </div>
                    </div>
                    <div v-if="!isEmpty(cartItemsGrouped)">
                      <div class="space-y-3.5 shadow-cart-subtotal p-4">
                        <div
                          v-for="info in cartInformation"
                          :key="info.label"
                          class="flex items-center justify-between text-sm font-light"
                        >
                          <div :class="cn(info.extraLabelClass)">
                            {{ info.label }}
                          </div>

                          <div :class="cn(info.extraValueClass)">
                            {{ info.value }}
                          </div>
                        </div>
                      </div>
                      <div class="px-4 pb-4">
                        <UiButton
                          color="white"
                          size="md"
                          :to="
                            localePath(
                              `/checkout/${generalStore.cart?.unique_id || ''}`,
                            )
                          "
                          class="w-full"
                          @click="handleCheckoutClick"
                        >
                          {{ $t("checkout") }}
                        </UiButton>
                      </div>
                    </div>
                  </template>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { onClickOutside } from "@vueuse/core";
import { Cart } from "ecom-types";

const checkoutStore = useCheckoutStore();
const generalStore = useGeneralStore();
const generalInternalStore = useGeneralInternalStore();
const {
  closeCart,
  cartItemsGrouped,
  cartInfo,
  cartItemsQuantity,
  cartItems,
  hasOnlyDigitalItemsInCart,
} = useCart();
const { t, n } = useI18n();
const dialog = ref(null);

const isMounted = useMounted();
const { viewCartEvent, beginCheckoutEvent } = useEvents();

const shipDate = computed(() => {
  return cartInfo.value?.ship_date || "";
});

const { estimateShipping } = useEstimateShipping(shipDate, "LLLL dd");

onClickOutside(dialog, () => {
  closeWithDelay();
});

watch(
  () => checkoutStore.isCartOpen,
  (val) => {
    generalInternalStore.systemOverlay = val;
  },
);

watchEffect(() => {
  if (isMounted.value && checkoutStore.isCartOpen) {
    viewCartEvent(cartInfo.value);
  }
});

const localePath = useLocalePathPolyfill();

const cartItemCount = computed(() => {
  return cartItemsQuantity.value ? `(${cartItemsQuantity.value})` : "";
});

function onAfterEnter() {
  setTimeout(() => {
    const inertEl = document.querySelector("[inert]");
    if (inertEl) {
      inertEl.removeAttribute("inert");
    }
    const hiddenEl = document.querySelector(".drawer-wrap [hidden]");
    if (hiddenEl) {
      hiddenEl.remove();
    }
  }, 0);
}

const cartInformation = computed(() => {
  const items = [
    {
      label: t("subtotal"),
      value: n(Number(cartInfo.value?.base_amount), "currency"),
      extraLabelClass: "uppercase font-bold",
      extraValueClass: "font-bold",
    },
  ];
  if (!hasOnlyDigitalItemsInCart.value) {
    items.push({
      label: t("shipping"),
      value: t("free"),
      extraLabelClass: "",
      extraValueClass: "",
    });
  }
  return items;
});

function closeWithDelay() {
  setTimeout(() => {
    checkoutStore.isCartOpen = false;
  }, 0);
}

function handleCheckoutClick() {
  closeCart();
  setBeginCheckoutEvents();
}
function setBeginCheckoutEvents() {
  beginCheckoutEvent({
    cartItems: cartItems.value,
    cartInfo: cartInfo.value as Cart,
  });
}
</script>
